import { ga } from '../../../engine/ga-sidecar'
import { websockets } from '../../../engine/websockets-sidecar'
import { assert } from '../../../lib/assert-sidecar'
import { l } from '../../../lib/imports'
import { clickEater } from '../../../uiParts/clickEater-sidecar'
import { stevieTexts2 } from '../../../uiParts/stevieTexts2-sidecar'
import { chessAiRando } from '../ai/chessAiRando-sidecar'
import { chessCamera } from '../chessCamera-sidecar'
import { chessGameRules } from '../chessGameRules-sidecar'
import { chessLayers } from '../chessLayers-sidecar'
import { EChessTeam } from '../chessPieces'
import { EChessClientRequest } from '../messages/chessMessageClientRequest'
import { chessMessageClientRequestResponse } from '../messages/chessMessageClientRequestResponse-sidecar'
import { chessFonts } from './chessFonts-sidecar'
import type { IChessUiCanvas } from './chessUiCanvas'
import { chessUiCanvas } from './chessUiCanvas-sidecar'
import { chessUiGButtons } from './chessUiGButtons-sidecar'
import { chessUiGraphicsUtils } from './chessUiGraphicsUtils-sidecar'
import type { IChessUiGSelector } from './chessUiGSelectors'
import { chessUiGSelectors } from './chessUiGSelectors-sidecar'

interface IChessUiSetupAi extends IChessUiCanvas {
  selectorTeam: IChessUiGSelector | null
  selectorDifficulty: IChessUiGSelector | null
  selectorGameSpeed: IChessUiGSelector | null
  selectorAiBrainSpeed: IChessUiGSelector | null
}
let _obj: IChessUiSetupAi | null = null
export function get() {
  if (!_obj) {
    _obj = create()
    createButtons()
    setOpen(false)
    render()
  }
  return _obj
}

const create = () => {
  let tw = 600
  let th = 700

  let canvas = chessUiCanvas.create(tw, th, chessLayers.getLayers().uiMessage)
  canvas.autoOffset = false

  let obj: IChessUiSetupAi = chessUiCanvas.assign<IChessUiSetupAi>(canvas, {
    selectorTeam: null,
    selectorDifficulty: null,
    selectorGameSpeed: null,
    selectorAiBrainSpeed: null,
  })

  return obj
}

export const moveTimes = ['0', '0.5', '1', '2', '3', '5', '10', '15', '30']

const createButtons = () => {
  let obj = get()
  obj.buttons = [
    chessUiGButtons.create('Cancel', 50, 50, () => {
      ga.action('click', 'ai-cancel')
      chessAiRando.killAll()
      setOpen(false)
    }),
    chessUiGButtons.create('Ok', 250, 50, () => {
      ga.action('click', 'ai-ok')
      setOpen(false)

      assert.notNil(obj.selectorTeam)
      assert.notNil(obj.selectorDifficulty)
      assert.notNil(obj.selectorAiBrainSpeed)
      let tv = obj.selectorTeam.value
      let team = tv === 'White' ? EChessTeam.White : EChessTeam.Black
      let youTeam =
        tv === 'White'
          ? EChessClientRequest.SitBlack
          : EChessClientRequest.SitWhite

      chessAiRando.killAll()

      let aiBrainSpeed = parseFloat(obj.selectorAiBrainSpeed.value)

      chessAiRando.create(team, obj.selectorDifficulty.value, aiBrainSpeed)
      chessMessageClientRequestResponse.sendMessageAndImmediatelyExecute({
        command: youTeam,
        multiId: websockets.getMultiId(),
      })

      assert.notNil(obj.selectorGameSpeed)
      let gameSpeed = parseFloat(obj.selectorGameSpeed.value)
      chessGameRules.setMoveTime(gameSpeed)
    }),
  ]

  let ais = [
    { name: 'Easy Eddie', gameSpeed: '10', aiBrainSpeed: '0.25' },
    { name: 'Slow Steve', gameSpeed: '10', aiBrainSpeed: '0.5' },
    { name: 'Randy Random', gameSpeed: '5', aiBrainSpeed: '1' },
    { name: 'Fast Francis', gameSpeed: '3', aiBrainSpeed: '2' },
    { name: 'Master Missy', gameSpeed: '2', aiBrainSpeed: '5' },
    { name: 'Impossible Ira', gameSpeed: '0', aiBrainSpeed: '1000' },
    { name: 'Custom Craig', gameSpeed: '5', aiBrainSpeed: '1' },
  ]
  let defaultAi = ais[2]
  let customAi = ais[ais.length - 1]

  let sw = 520
  let lw = 250

  obj.selectors = [
    (obj.selectorTeam = chessUiGSelectors.create(
      'AI Team',
      ['Black', 'White'],
      100,
      100,
      sw,
      lw,
    )),
    (obj.selectorDifficulty = chessUiGSelectors.create(
      'Difficulty',
      l.map(ais, (c) => c.name),
      100,
      100,
      sw,
      lw,
    )),
    (obj.selectorGameSpeed = chessUiGSelectors.create(
      'Movement Delay',
      moveTimes,
      100,
      100,
      sw,
      lw,
    )),
    (obj.selectorAiBrainSpeed = chessUiGSelectors.create(
      'AI Brain Speed',
      [
        '0.10',
        '0.25',
        '0.5',
        '0.75',
        '1',
        '1.25',
        '1.5',
        '1.75',
        '2',
        '2.5',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '1000',
      ],
      100,
      100,
      sw,
      lw,
    )),
  ]

  obj.selectorDifficulty.onChange = () => {
    assert.notNil(obj.selectorDifficulty)
    let v = l.find(ais, (c) => c.name === obj.selectorDifficulty?.value)
    if (v) {
      assert.notNil(obj.selectorGameSpeed)
      assert.notNil(obj.selectorAiBrainSpeed)
      chessUiGSelectors.setValue(obj.selectorGameSpeed, v.gameSpeed)
      chessUiGSelectors.setValue(obj.selectorAiBrainSpeed, v.aiBrainSpeed)
    }
  }
  obj.selectorGameSpeed.onChange = () => {
    assert.notNil(obj.selectorDifficulty)
    chessUiGSelectors.setValue(obj.selectorDifficulty, customAi.name)
  }
  obj.selectorAiBrainSpeed.onChange = () => {
    assert.notNil(obj.selectorDifficulty)
    chessUiGSelectors.setValue(obj.selectorDifficulty, customAi.name)
  }

  chessUiGSelectors.setValue(obj.selectorDifficulty, defaultAi.name)
  chessUiGSelectors.setValue(obj.selectorGameSpeed, defaultAi.gameSpeed)
  chessUiGSelectors.setValue(obj.selectorAiBrainSpeed, defaultAi.aiBrainSpeed)
}

const render = () => {
  let obj = get()
  let g = obj.graphics

  chessUiCanvas.clear(obj)

  let addText = (text: string, x: number, y: number) => {
    let _text = stevieTexts2.create(chessFonts.get().f, x, y, obj.layer)
    stevieTexts2.setText(_text, text)
    stevieTexts2.setScale(_text, 0.4)
    obj.texts.push(_text)
    return _text
  }

  g.clear()

  let gb = chessCamera.getGameBounds()

  g.fillStyle(0x222222, 0.67)
  g.fillRect(0, 0, gb.width, gb.height)

  let w = obj.targetWidth
  let h = obj.targetHeight

  let x = (gb.width - w) / 2
  let y = (gb.height - h) / 2

  chessUiGraphicsUtils.bevelPanel(g, x, y, w, h)

  let title = addText('Setup AI', x + 6, y)
  stevieTexts2.setScale(title, 0.6)

  x += 10
  y += 40
  obj.selectors[0].x = x
  obj.selectors[0].y = y

  y += 60
  obj.selectors[1].x = x
  obj.selectors[1].y = y

  y += 60
  obj.selectors[2].x = x
  obj.selectors[2].y = y

  y += 60
  obj.selectors[3].x = x
  obj.selectors[3].y = y

  x = (gb.width - w) / 2
  y = (gb.height - h) / 2
  x += 20
  y += h - 60
  obj.buttons[0].x = x
  obj.buttons[0].y = y
  obj.buttons[1].x = x + 200
  obj.buttons[1].y = y

  chessUiCanvas.render(obj)
}

export const setOpen = (isOpen: boolean, onOk = () => {}) => {
  let obj = get()
  chessUiCanvas.setOpen(obj, isOpen)
  if (isOpen) {
    update()
  }
}

export const update = () => {
  let obj = get()

  if (obj.isOpen) {
    chessUiCanvas.update(obj)
    render()
    clickEater.tempEatCursor()
  } else {
    chessUiCanvas.clear(obj)
  }
}
